import { useBusinessContext } from '@context'
import { useQueryParams } from '@hooks'
import { bankIntegrationDataSources, getImportSourceLabelKey } from '@root/query/documents'
import { IAccountingSuggestion } from '@root/query/suggestions'
import { formatCurrency, formatDate } from '@utils'
import React, { useEffect, useId, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  FaAddressCard,
  FaCheck,
  FaGraduationCap,
  FaInfoCircle,
  FaMinus,
  FaUniversity
} from 'react-icons/fa'
import {
  FaCircleCheck,
  FaCircleExclamation,
  FaCircleMinus,
  FaCircleQuestion,
  FaFileLines,
  FaXmark
} from 'react-icons/fa6'
import { useTheme } from 'styled-components'
import {
  Eyebrow,
  UncertaintyBasisLink,
  UncertaintyBasisRow,
  UncertaintyBasisText,
  UncertaintyCheckButton,
  UncertaintyCheckList,
  UncertaintyCheckRow,
  UncertaintyCheckStatus,
  UncertaintyCheckTitle,
  UncertaintyCloseButton,
  UncertaintyConfirmLink,
  ConfidenceRingLabel,
  ConfidenceRingSvg,
  ConfidenceRingTrack,
  ConfidenceRingValue,
  UncertaintyNeedBox,
  UncertaintyNeedRow,
  UncertaintyNeedText,
  UncertaintyPanelCard,
  UncertaintyPanelHeader,
  UncertaintyPanelSection,
  UncertaintyPanelSummary,
  UncertaintyPanelTitle,
  UncertaintyPanelTitleBlock,
  UncertaintyScore,
  UncertaintyPopoverAnchor,
  UncertaintyPopoverButton,
  UncertaintyPopoverPanel,
  UncertaintyWhy
} from '../styles'
import { UNCERTAINTY_SEVERITY, UncertaintyCode } from './model'
import { uncertaintySection, useSuggestionUncertainty } from './SuggestionUncertaintyContext'

interface SuggestionUncertaintyPopoverProps {
  suggestion: IAccountingSuggestion
}

const statusIcon = (code: UncertaintyCode, isIssue: boolean) => {
  if (!isIssue) return FaCircleCheck
  if (UNCERTAINTY_SEVERITY[code] === 'error') return FaCircleExclamation
  if (UNCERTAINTY_SEVERITY[code] === 'info') return FaInfoCircle
  return FaCircleMinus
}

const RING_SIZE = 36
const RING_STROKE = 3.5
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS
const CONFIDENCE_CAP = 99

const displayedConfidence = (value: number) =>
  Math.max(0, Math.min(CONFIDENCE_CAP, Math.round(value)))

const confidenceTone = (value: number): 'high' | 'mid' | 'low' => {
  if (value >= 80) return 'high'
  if (value >= 50) return 'mid'
  return 'low'
}

const ConfidenceRing: React.FC<{ value: number }> = ({ value }) => {
  const shown = displayedConfidence(value)
  const offset = RING_CIRCUMFERENCE * (1 - shown / 100)
  const center = RING_SIZE / 2
  return (
    <>
      <ConfidenceRingSvg viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`} aria-hidden>
        <ConfidenceRingTrack cx={center} cy={center} r={RING_RADIUS} strokeWidth={RING_STROKE} />
        <ConfidenceRingValue
          cx={center}
          cy={center}
          r={RING_RADIUS}
          strokeWidth={RING_STROKE}
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={offset}
        />
      </ConfidenceRingSvg>
      <ConfidenceRingLabel>{shown}%</ConfidenceRingLabel>
    </>
  )
}

export const SuggestionUncertaintyPopover: React.FC<SuggestionUncertaintyPopoverProps> = ({
  suggestion
}) => {
  const { t } = useTranslation()
  const { slug } = useBusinessContext()
  const [, , getUrlWithParams] = useQueryParams()
  const theme = useTheme()
  const panelId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const { items, issueCount, hasError, isOpen, dismiss, flash, focusAttachments } =
    useSuggestionUncertainty()

  useEffect(() => {
    if (!open) return undefined
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const importSourceLabel = t(
    `document.importSource.hoverTitle.${getImportSourceLabelKey(
      suggestion.import_data.data_source
    )}`
  )
  const hasDocument = !isOpen('missing_attachment')
  const noSeed = (suggestion.uncertainty ?? []).includes('no_seed')
  const historyDocumentId = suggestion.history_document_id
  const summaryTone = issueCount === 0 ? 'ready' : hasError ? 'error' : 'review'
  const summary = issueCount
    ? t('document.suggestionCard.review.summaryChecked', {
        done: items.length - issueCount,
        total: items.length
      })
    : t('document.suggestionCard.review.summaryReady')

  const confirm = (code: UncertaintyCode) => {
    if (code === 'missing_attachment') {
      setOpen(false)
      focusAttachments()
      return
    }
    dismiss(code)
  }

  const basis = [
    {
      key: 'bank',
      icon: FaUniversity,
      text: t('document.suggestionCard.review.basis.bank', {
        source: importSourceLabel,
        date: formatDate(suggestion.date),
        amount: formatCurrency(suggestion.amount)
      }),
      used: true,
      href: bankIntegrationDataSources.includes(suggestion.import_data.data_source)
        ? getUrlWithParams({ tab: 'import_data' })
        : undefined
    },
    {
      key: 'contact',
      icon: FaAddressCard,
      text: suggestion.contact_name
        ? t('document.suggestionCard.review.basis.contact')
        : t('document.suggestionCard.review.basis.noContact'),
      used: Boolean(suggestion.contact_name),
      href: undefined
    },
    {
      key: 'history',
      icon: FaInfoCircle,
      text: noSeed
        ? t('document.suggestionCard.review.basis.noHistory')
        : t('document.suggestionCard.review.basis.history'),
      used: !noSeed,
      href:
        !noSeed && historyDocumentId != null
          ? `/${slug}/documents?id=${historyDocumentId}`
          : undefined
    },
    {
      key: 'document',
      icon: FaFileLines,
      text: hasDocument
        ? t('document.suggestionCard.review.basis.document')
        : t('document.suggestionCard.review.basis.noDocument'),
      used: hasDocument,
      href: undefined
    }
  ]

  const score =
    typeof suggestion.confidence === 'number' ? displayedConfidence(suggestion.confidence) : null

  return (
    <UncertaintyPopoverAnchor ref={rootRef}>
      <UncertaintyPopoverButton
        type="button"
        $tone={score == null ? 'mid' : confidenceTone(score)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={
          score == null
            ? t('document.suggestionCard.review.popoverTitle')
            : t('document.suggestionCard.review.confidenceLabel', { score })
        }
        onClick={() => setOpen(current => !current)}
      >
        {score == null ? (
          <FaCircleQuestion aria-hidden />
        ) : (
          <ConfidenceRing value={suggestion.confidence as number} />
        )}
      </UncertaintyPopoverButton>
      {open && (
        <UncertaintyPopoverPanel id={panelId}>
          <UncertaintyPanelCard>
            <UncertaintyPanelSection>
              <UncertaintyPanelHeader>
                <UncertaintyPanelTitleBlock>
                  <UncertaintyPanelTitle>
                    {t('document.suggestionCard.review.popoverTitle')}
                  </UncertaintyPanelTitle>
                  {score == null && (
                    <UncertaintyPanelSummary $tone={summaryTone}>{summary}</UncertaintyPanelSummary>
                  )}
                </UncertaintyPanelTitleBlock>
                <UncertaintyCloseButton type="button" onClick={() => setOpen(false)}>
                  <FaXmark aria-hidden />
                </UncertaintyCloseButton>
              </UncertaintyPanelHeader>
              {score != null && (
                <UncertaintyWhy>
                  <Trans
                    i18nKey="document.suggestionCard.review.confidenceExplanation"
                    values={{ score }}
                    components={{
                      score: <UncertaintyScore $tone={confidenceTone(score)} />
                    }}
                  />
                </UncertaintyWhy>
              )}
            </UncertaintyPanelSection>

            <UncertaintyPanelSection>
              <Eyebrow>{t('document.suggestionCard.review.basisTitle')}</Eyebrow>
              {basis.map(row => {
                const Icon = row.icon
                return (
                  <UncertaintyBasisRow key={row.key}>
                    <Icon aria-hidden />
                    {row.href ? (
                      <UncertaintyBasisLink to={row.href} onClick={() => setOpen(false)}>
                        {row.text}
                      </UncertaintyBasisLink>
                    ) : (
                      <UncertaintyBasisText $used={row.used}>{row.text}</UncertaintyBasisText>
                    )}
                    {row.used ? (
                      <FaCheck aria-hidden color={theme.colors.success} />
                    ) : (
                      <FaMinus aria-hidden color={theme.colors.iconGray} />
                    )}
                  </UncertaintyBasisRow>
                )
              })}
            </UncertaintyPanelSection>

            <UncertaintyCheckList>
              <Eyebrow>{t('document.suggestionCard.review.checksTitle')}</Eyebrow>
              {items.map(item => {
                const Icon = statusIcon(item.code, item.isIssue)
                const color = !item.isIssue
                  ? theme.colors.success
                  : UNCERTAINTY_SEVERITY[item.code] === 'error'
                  ? theme.colors.error
                  : UNCERTAINTY_SEVERITY[item.code] === 'info'
                  ? theme.colors.iconGray
                  : theme.colors.warning
                return (
                  <UncertaintyCheckRow key={item.code}>
                    <Icon aria-hidden color={color} style={{ marginTop: 2 }} />
                    <UncertaintyCheckButton
                      type="button"
                      onClick={() => {
                        setOpen(false)
                        flash(uncertaintySection(item.code))
                      }}
                    >
                      <UncertaintyCheckTitle>
                        {t(`document.suggestionCard.review.items.${item.code}.title`)}
                      </UncertaintyCheckTitle>
                      <UncertaintyCheckStatus>
                        {item.isIssue
                          ? t(`document.suggestionCard.uncertainty.${item.code}`)
                          : t(`document.suggestionCard.review.items.${item.code}.ok`)}
                      </UncertaintyCheckStatus>
                    </UncertaintyCheckButton>
                    {item.isIssue && (
                      <UncertaintyConfirmLink type="button" onClick={() => confirm(item.code)}>
                        {t(`document.suggestionCard.review.items.${item.code}.short`)}
                      </UncertaintyConfirmLink>
                    )}
                  </UncertaintyCheckRow>
                )
              })}
            </UncertaintyCheckList>

            {!isOpen('missing_attachment') && isOpen('no_seed') && (
              <UncertaintyNeedBox>
                <UncertaintyNeedRow>
                  <FaGraduationCap aria-hidden />
                  <UncertaintyNeedText>
                    {t('document.suggestionCard.review.seedHint')}
                  </UncertaintyNeedText>
                </UncertaintyNeedRow>
              </UncertaintyNeedBox>
            )}
          </UncertaintyPanelCard>
        </UncertaintyPopoverPanel>
      )}
    </UncertaintyPopoverAnchor>
  )
}
